import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBlueMonk } from '@/contexts/BlueMonkContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';

const BlueMonkAdminLogin = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const { adminLogin, isAdminAuthenticated } = useBlueMonk();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isAdminAuthenticated()) {
        navigate('/bluemonk/admin/dashboard');
      }
      setChecking(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [isAdminAuthenticated, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await adminLogin(identifier, password);
      toast.success('Welcome to BlueMonk Admin');
      navigate('/bluemonk/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required. Contact your supervisor.');
      } else {
        toast.error('Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="w-full max-w-md p-8 bg-white shadow-xl">
          <div className="text-center mb-8">
            <div className="bg-white rounded-xl p-4 mx-auto mb-6 inline-block shadow-sm border">
              <img 
                src="/logo.png" 
                alt="Content Vista" 
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Lock className="h-5 w-5 text-slate-500" />
              <h1 className="text-xl font-bold text-slate-900">BlueMonk Admin</h1>
            </div>
            <p className="text-slate-500">Content Vista Team Access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="identifier" className="text-sm font-semibold">Email or Username</Label>
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="mt-1.5"
                placeholder="your.email@contentvista.com"
                autoFocus
                data-testid="admin-identifier-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
                placeholder="Enter your password"
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 mt-6"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-xs text-slate-400">
              Use your Content Vista dashboard credentials
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default BlueMonkAdminLogin;
